import { PATHS } from '@/shared/constants/app/routes.constant.ts';

/** ?from= 쿼리 파라미터 키. client.ts(onUnauthorized)와 공유 → 단일 소스 */
export const REDIRECT_PARAM = 'from';

/** 복귀 대상이 없을 때 기본 이동 경로 */
export const DEFAULT_REDIRECT = PATHS.HOME;

/** 복귀 대상에서 제외할 경로 (로그인 루프/auth 페이지 방지) */
const DISALLOWED_PREFIXES: readonly string[] = [
  PATHS.LOGIN,
  PATHS.SIGNUP,
  PATHS.REQUEST_PASSWORD_RESET,
  PATHS.RESET_PASSWORD,
  PATHS.VERIFY_EMAIL,
  PATHS.CONFIRM_EMAIL_CHANGE,
  PATHS.UNAUTHORIZED,
  // 온보딩 중 세션이 끊겨 재로그인하면 from 이 온보딩 자신이 된다 — 복귀 대상이 아니다.
  PATHS.ONBOARDING,
  PATHS.RECONSENT,
];

/** AuthGuard 가 state로 넘기는 location 형태(필요 필드만) */
export interface RedirectStateFrom {
  pathname?: string;
  search?: string;
}

/**
 * 내부 경로 여부 검증 (open redirect 방지).
 * '/'로 시작 + '//'(프로토콜 상대) 아님 + 스킴(://) 미포함만 허용.
 */
export function isSafeInternalPath(
  path: string | null | undefined,
): path is string {
  if (!path || !path.startsWith('/') || path.startsWith('//')) return false;
  return !path.includes('://');
}

function isAuthPath(path: string): boolean {
  return DISALLOWED_PREFIXES.some(
    (p) => path === p || path.startsWith(`${p}?`) || path.startsWith(`${p}/`),
  );
}

interface ResolveOptions {
  /** AuthGuard 가 넘긴 location.state.from */
  stateFrom?: RedirectStateFrom | null;
  /** ?from= 쿼리 값 */
  queryFrom?: string | null;
  /** 채택 가능한 후보가 없을 때 이동할 경로 (기본 HOME) */
  fallback?: string;
}

/**
 * "다음에 갈 경로"를 결정한다. 로그인/게스트 가드 등에서 공용 사용.
 *
 * 우선순위: stateFrom → queryFrom → fallback
 * - 각 후보는 내부 경로 검증을 통과하고 auth 계열이 아니어야 채택
 * - 새로운 진입점은 fallback만 바꿔 끼우면 됨 (확장 지점)
 */
export function resolveRedirectTarget({
  stateFrom,
  queryFrom,
  fallback = DEFAULT_REDIRECT,
}: ResolveOptions): string {
  const candidates: Array<string | undefined> = [
    stateFrom
      ? `${stateFrom.pathname ?? ''}${stateFrom.search ?? ''}`
      : undefined,
    queryFrom ?? undefined,
  ];

  for (const candidate of candidates) {
    if (isSafeInternalPath(candidate) && !isAuthPath(candidate)) {
      return candidate;
    }
  }
  return fallback;
}
